import React from 'react';
import {
  Box,
  Container,
  Divider,
  Grid,            // ⬅️ Importado para el BackButton
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/system';
import theme from '../../styles/StyleTheme';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import BackButton from '../../components/BackButton';  // ⬅️ Importa tu BackButton

const ContentWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.shape.borderRadius * 3,
  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.18)',
  padding: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

const AccentDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  height: '4px',
  width: '80px',
  margin: theme.spacing(3, 'auto', 5),
  borderRadius: '999px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.gray1 || '#323232',
  marginBottom: theme.spacing(2),
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  color: theme.palette.gray2 || '#595959',
  marginBottom: theme.spacing(2),
  lineHeight: 1.7,
}));

const Methodology = () => {
  const informationSources = [
    'Datos proporcionados por las propias firmas mediante formularios estructurados.',
    'Información pública de fuentes confiables (sitios web, reportes de prensa, redes sociales, registros oficiales).',
    'Entrevistas con clientes y otros actores relevantes del sector legal.',
  ];

  const verificationAndNormalization = [
    'Revisión cruzada de datos para garantizar exactitud.',
    'Estandarización de métricas para asegurar comparabilidad entre jurisdicciones y tamaños de firma.',
  ];

  const scoreCalculation = [
    'Aplicación de un modelo estadístico propietario que pondera múltiples dimensiones clave.',
    'Respaldo con análisis comparativo respecto de los principales rankings internacionales, adaptado al mercado latinoamericano y global.',
    'Por razones de confidencialidad, las ponderaciones exactas y escalas no se publican.',
  ];

  const reviewAndPublication = [
    'Validación de resultados por el equipo de Legal Monitor antes de su publicación.',
    'Actualizaciones periódicas incorporando nuevos datos y corrigiendo información obsoleta.',
  ];

  const dimensions = [
    'Tamaño de la Firma (Size): Estructura y capacidad operativa.',
    'Reputación: Reconocimiento en el sector y posicionamiento en el mercado.',
    'Presencia de Marca (Brand Power): Alcance y relevancia en medios y redes profesionales.',
    'Cartera de Clientes (Clients): Diversidad, calidad y nivel de los clientes representados.',
    'Trayectoria (Trajectory): Evolución histórica, crecimiento y estabilidad.',
    'Ámbito Geográfico (Arena): Alcance territorial y capacidad para operar en múltiples jurisdicciones.',
    'Trabajo Directo (Hands-on Work): Magnitud e impacto de los asuntos manejados.',
  ];

  const principles = [
    'Transparencia: Explicamos qué dimensiones evaluamos y por qué son relevantes.',
    'Equidad: El modelo no favorece exclusivamente a firmas grandes; reconoce excelencia en distintos contextos y escalas.',
    'Actualización Constante: Los datos se revisan a lo largo del año para reflejar cambios reales.',
    'Inclusividad: Consideramos variables usualmente excluidas por rankings tradicionales (innovación, diversidad, presencia digital).',
    'Independencia: No existe relación comercial que condicione los resultados.',
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(15, 29, 40, 0.82), rgba(15, 29, 40, 0.82)), url(${buildingsBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          {/* ⬅️ Botón Back en esquina superior izquierda */}
          <Grid container justifyContent="start" sx={{ mt: 1, ml: 0, mb: 2 }}>
            <BackButton path="/market-monitor-legal" />
          </Grid>

          <ContentWrapper>
            <Typography
              variant="h3"
              component="h1"
              align="center"
              sx={{
                fontWeight: 700,
                color: theme.palette.gray1 || '#1f1f1f',
                mb: 1,
              }}
            >
              Metodología Legal Monitor
            </Typography>
            <Typography
              align="center"
              sx={{
                color: theme.palette.gray3 || '#75838E',
                fontSize: '1.05rem',
              }}
            >
              Transparencia, consistencia e inclusión para comprender el mercado legal.
            </Typography>

            <AccentDivider />

            {/* Visión General */}
            <SectionTitle variant="h5" component="h2">
              Visión General
            </SectionTitle>
            <Paragraph>
              Legal Monitor es una plataforma independiente dedicada a evaluar y jerarquizar firmas legales en base a criterios
              objetivos, verificables y actualizados de forma continua. Nuestro objetivo es ofrecer al mercado una herramienta
              transparente, inclusiva y dinámica, que supere las limitaciones de los rankings tradicionales, incorporando nuevas
              variables y un enfoque estadístico sólido.
            </Paragraph>
            <Paragraph>
              La metodología integra fuentes de información pública y privada, así como datos aportados directamente por las
              firmas, entrevistas con clientes y verificación por parte de nuestro equipo de análisis. Todo se procesa bajo un
              modelo propio de evaluación diseñado para reflejar desempeño, reputación, proyección y competitividad en el mercado
              legal.
            </Paragraph>

            {/* Ámbito y Cobertura */}
            <SectionTitle variant="h5" component="h2">
              Ámbito y Cobertura
            </SectionTitle>
            <Paragraph>
              Legal Monitor cubre firmas de todos los tamaños y especialidades, en múltiples jurisdicciones y áreas de práctica.
              La evaluación se realiza tanto a nivel nacional como regional, permitiendo comparaciones dentro de un país y análisis
              transfronterizos.
            </Paragraph>
            <Paragraph>
              Nuestro sistema está diseñado para reconocer tanto a las firmas consolidadas como a las emergentes, identificando
              oportunidades de crecimiento y tendencias en el mercado.
            </Paragraph>

            {/* Proceso de Evaluación */}
            <SectionTitle variant="h5" component="h2">
              Proceso de Evaluación
            </SectionTitle>

            <Paragraph sx={{ fontWeight: 700, mt: 1 }}>1) Recopilación de Información</Paragraph>
            <List>
              {informationSources.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            <Paragraph sx={{ fontWeight: 700, mt: 1 }}>2) Verificación y Normalización</Paragraph>
            <List>
              {verificationAndNormalization.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            <Paragraph sx={{ fontWeight: 700, mt: 1 }}>3) Cálculo del Score</Paragraph>
            <List>
              {scoreCalculation.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            <Paragraph sx={{ fontWeight: 700, mt: 1 }}>4) Revisión y Publicación</Paragraph>
            <List>
              {reviewAndPublication.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            {/* Dimensiones de Evaluación */}
            <SectionTitle variant="h5" component="h2">
              Dimensiones de Evaluación
            </SectionTitle>
            <Paragraph>
              El modelo analiza siete dimensiones principales que, en conjunto, ofrecen una visión integral de cada firma:
            </Paragraph>
            <List>
              {dimensions.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            {/* Principios Clave */}
            <SectionTitle variant="h5" component="h2">
              Principios Clave de la Metodología
            </SectionTitle>
            <List>
              {principles.map((item) => (
                <ListItem key={item} sx={{ alignItems: 'flex-start', pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                    <CheckCircleOutlineIcon sx={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{
                      sx: { color: theme.palette.gray2 || '#4f5b65', lineHeight: 1.6 },
                    }}
                    primary={item}
                  />
                </ListItem>
              ))}
            </List>

            {/* Frecuencia de Actualización */}
            <SectionTitle variant="h5" component="h2">
              Frecuencia de Actualización
            </SectionTitle>
            <Paragraph>
              Legal Monitor revisa sus evaluaciones en ciclos periódicos, con la posibilidad de ajustes intermedios cuando
              existan cambios relevantes en la información de una firma. Esto permite que el ranking refleje tendencias en tiempo
              real y no solo “snapshots” anuales.
            </Paragraph>

            {/* Confidencialidad y Seguridad */}
            <SectionTitle variant="h5" component="h2">
              Confidencialidad y Seguridad del Modelo
            </SectionTitle>
            <Paragraph>
              El sistema de cálculo de Legal Monitor está protegido para garantizar que no pueda ser manipulado externamente. La
              fórmula exacta y las ponderaciones precisas son confidenciales, aunque la estructura general y las dimensiones
              evaluadas son de dominio público para asegurar transparencia metodológica.
            </Paragraph>
          </ContentWrapper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Methodology;